import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { SelectionType } from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { AddressesForward } from "src/model/class/general classes/addresses-forward";
import { GenericTableAnagraphTable } from "src/model/class/general classes/generic-table-anagraph-table";
import { Column, TableInfo, ValueToChange } from "src/components/shared-components/class/general-classes/item-history";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { ColumnTableType, HideShowColumn } from "src/components/shared-components/enum/enumTableHistory";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iGenericTable } from "src/model/interface/iGeneric-table";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AddressesForwardService } from "src/service/addresses-forward.service";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { MqttClientService } from "src/service/mqtt-client.service";
import { NetworkService } from "src/service/network.service";

@Component({
    selector: "app-addresses-forward-list",
    templateUrl: "./addresses-forward-list.component.html",
    styleUrls: ["./addresses-forward-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddressesForwardListComponent
  extends GenericTableAnagraphTable
  implements OnInit, OnDestroy, iTranslation, iEvent, iGenericTable {
  private _logContext: string = "Addresses-forward-list.component";
  private _addressesForwardSubscription: Subscription = null;

  //#region Translation
  private _translateSubscription: Subscription = null;
  private _description: string = "description";
  private _destinationAddress: string = "destinationAddress";
  private _destinationGateway: string = "destinationGateway";
  private _factor: string = "factor";
  private _lblNew: string = "";
  private _offset: string = "offset";
  private _sourceAddress: string = "sourceAddress";
  private _sourceGateway: string = "sourceGateway";
  //#endregion

  private _active: string = "active";

  private _networkSubscription: Subscription = null;
  private _intervalTime: number = 4000;
  private _addressesForward: Array<AddressesForward> = new Array<AddressesForward>();

  public _eventSubscriptions: Subscription[] = null;

  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  public tableInfo: TableInfo = null;
  public selected: Array<any> = new Array<any>();

  constructor(
    private _loaderController: LoaderService,
    private _alertService: AlertService,
    private _addressesForwardService: AddressesForwardService,
    private _loggerService: LoggerService,
    private _eventService: EventService,
    private _mqttService: MqttClientService,
    private _translateService: TranslateService,
    private _networkService: NetworkService
  ) {
    super();
  }

  ngOnInit(): void {
    this.tableInfo = StaticClass.createTableInfo("material expandable", false, SelectionType.single, this.createArrayColumns(), false, true);
    this.tableInfo.IsListInitialized = false;
    this.tableInfo.RowHeight = "auto";

    this.subscribeEvents();
    this.loadTranslation();
    this.subscribeTranslationEvent();

    this.subscribeAddressesForwardService();
    this.subscribeNetworkServiceData();

    this._addressesForwardService.Init();
  }

  ngOnDestroy(): void {
    this._addressesForwardSubscription.unsubscribe();
    this._translateSubscription.unsubscribe();
    this._networkSubscription.unsubscribe();
    this.unsubscribeEvents();
  }

  public OnChanges(): void {
    if (!this.CurrentObject) {
      this.selected = [];
    }
  }

  //#region Subscribe to event service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnAddressesForwardChange.pipe(
        filter(() => this.allowDetectionListChange)
      ).subscribe(async (idAddressesForwardChanged_ClientID: string) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnAddressesForwardChange",
          "AllowDetectionList: " +
          this.allowDetectionListChange +
          " | Received OnAddressesForwardChange from client: " +
          idAddressesForwardChanged_ClientID
        );

        const idAddressesForwardChanged_ClientIDSplit =
          idAddressesForwardChanged_ClientID.split("|");
        if (
          idAddressesForwardChanged_ClientIDSplit[1] !==
          this._mqttService.clientID
        ) {
          await this._alertService.presentAlertDataUpdated();
        }
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Create a new addressForward.
   */
  public async onRequestNew(): Promise<void> {
    await this._loaderController.presentLoaderWaiting();

    try {
      let newAddressesForward: AddressesForward = new AddressesForward();
      newAddressesForward.description = this._lblNew;

      newAddressesForward =
        await this._addressesForwardService.InsertAddressesForward(
          newAddressesForward
        );

      await this._loaderController.dismissLoaderWaiting();
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "onRequestNew()",
        "ERROR: " + error.message
      );

      await this._loaderController.dismissLoaderWaiting();

      await this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * Receives the selected item in the table and issues it to the form.
   * @param selected 
   */
  public selectionChanged(selected: any): void {
    let selectedAdd: AddressesForward = this._addressesForward.find(add => add.id === selected[0].id);
    if (selectedAdd) {
      this.selected = [];
      this.selected.push(selected[0]);
      this.changedSelected.emit(selectedAdd);
    }
  }

  /**
   * Reloads the data.
   * @param reload 
   */
  public reloadData(reload: string): void {
    this._addressesForwardService.reloadServiceData();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.newMale",
      ])
      .subscribe((translation) => {
        this._lblNew = translation["dictionary.newMale"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Creates the array containing the objects to show in the app view.
   */
  public createItemHistoryList(): void {
  }

  /**
   * Creates and returns the columns to pass to the table.
   */
  public createArrayColumns(): Column[] {
    let columns: Column[] = [];
    columns.push(StaticClass.createColumnObj(this._description, this._description, "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("instanceGUIDSourceAddress", this._sourceGateway, "", ColumnTableType.INSTANCEGUID, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj(this._sourceAddress, this._sourceAddress, "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj("instanceGUIDDestinationAddress", this._destinationGateway, "", ColumnTableType.INSTANCEGUID, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj(this._destinationAddress, this._destinationAddress, "", ColumnTableType.STRING, HideShowColumn.SHOW));
    columns.push(StaticClass.createColumnObj(this._active, "attivo_weekly", "", ColumnTableType.ICON, HideShowColumn.HIDE_1000));
    columns.push(StaticClass.createColumnObj("logicDescription", "logicUsed", "", ColumnTableType.STRING, HideShowColumn.HIDE_1000));
    columns.push(StaticClass.createColumnObj(this._factor, this._factor, "", ColumnTableType.ROUND_NUMBER_2, HideShowColumn.HIDE_1000));
    columns.push(StaticClass.createColumnObj(this._offset, this._offset, "", ColumnTableType.ROUND_NUMBER_2, HideShowColumn.HIDE_1000));
    return columns;
  }

  /**
   * Creates the table rows.
   */
  public createItemHistoryDesktopList(): void {
    this.tableInfo.List = [];
    let valuesToChange: ValueToChange[] = [];
    let addForwardCopy: AddressesForward[] = this._addressesForward;
    for (let i = 0; i < addForwardCopy.length; i++) {
      if (addForwardCopy[i].sourceAddress !== "") {
        valuesToChange.push(StaticClass.createValueToChange(this._sourceAddress, i, addForwardCopy[i].nameSourceAddress + " - " + addForwardCopy[i].sourceAddress));
      }
      if (addForwardCopy[i].destinationAddress !== "") {
        valuesToChange.push(StaticClass.createValueToChange(this._destinationAddress, i, addForwardCopy[i].nameDestinationAddress + " - " + addForwardCopy[i].destinationAddress));
      }
      valuesToChange.push(StaticClass.createValueToChange(this._active, i, StaticClass.createSubtitle("", "", addForwardCopy[i].active ? "checkmark-sharp" : "close-sharp", "")));
    }
    this.tableInfo.List = StaticClass.createArrayForTable(addForwardCopy, valuesToChange);
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );

          this._addressesForward = [...this._addressesForward];
        }
      );
  }

  private subscribeAddressesForwardService() {
    var timer = setTimeout(() => {
      this.handleApiResponseError();
    }, this._intervalTime);
    this._addressesForwardSubscription =
      this._addressesForwardService.addressesForwardList
        .pipe(
          filter((res) => res !== null),
          map((listAddresses) => {
            return listAddresses || [];
          })
        )
        .subscribe(async (listAddressesForward: Array<AddressesForward>) => {
          clearTimeout(timer);
          if (
            this._addressesForwardService
              .isErrorInGetAddressesForwardListAPIRequest
          ) {
            this.tableInfo.IsListInitialized = false;

            this.handlePeculiarApiResponseError();
          } else {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_addressesForwardService",
              "addressesForwardList",
              "All right"
            );

            this._addressesForward = listAddressesForward;
            this.tableInfo.IsListInitialized = true;
            this.createItemHistoryDesktopList();
            this.tableInfo.ShowErrorContent = false;
            if (this.selected.length > 0) {
              let updateAddForward = this.tableInfo.List.find(
                (y: AddressesForward) => y.id === this.selected[0].id
              );
              this.selected = [];
              this.selected.push(updateAddForward);
            }
          }
        });
  }

  private async handleApiResponseError(): Promise<void> {
    this.tableInfo.ShowErrorContent = true;
    await this._alertService.presentAlertSomethingWrong();
  }

  /**
   * Handle errors in api call which require peculiar reactions from the page; this includes cases where the error is propagated through subject.error()
   */
  private async handlePeculiarApiResponseError() {
    if (this._addressesForwardService.isErrorNotSupported) {
      this.tableInfo.IsFunctionalityNotImplemented = true;
    } else {
      this.handleApiResponseError();
    }
  }
}
